/**
 * Unit Pricing Calculator
 * Parses variant titles to extract quantity and unit, then calculates per-unit pricing
 */

class UnitPricing {
  /**
   * Parse variant title and calculate unit pricing
   * @param {string} variantTitle - The variant title (e.g., "5lb Package")
   * @param {number} variantPrice - The variant price in cents
   * @param {string} moneyFormat - Shopify money format string
   * @returns {string|null} - Formatted string with unit pricing or null if parsing fails
   */
  static calculate(variantTitle, variantPrice, moneyFormat) {
    if (!variantTitle || variantPrice === undefined || variantPrice === null) {
      console.warn('[UnitPricing] Invalid input:', { variantTitle, variantPrice });
      return null;
    }

    // Regex to extract quantity and unit (supports decimals, case-insensitive)
    // Pattern: number (with optional decimal) + optional space + unit
    const pattern = /(\d+\.?\d*)\s*(lb|oz|kg|g|gal|l|ml|fl\s?oz)/i;
    const match = variantTitle.match(pattern);

    if (!match) {
      console.warn('[UnitPricing] Could not parse unit from variant title:', variantTitle);
      return null;
    }

    const quantity = parseFloat(match[1]);
    const unit = match[2]; // Preserve original case from variant title

    if (quantity === 0) {
      console.warn('[UnitPricing] Zero quantity detected:', variantTitle);
      return null;
    }

    // Format variant price using Shopify money format
    const formattedVariantPrice = this.formatMoney(variantPrice, moneyFormat);

    // Extract just the quantity + unit portion (remove "package", "Package", etc.)
    const quantityUnitPart = `${match[1]}${unit}`;

    // Special case: if quantity is 1, don't show unit pricing
    if (quantity === 1) {
      return `${quantityUnitPart} – ${formattedVariantPrice}`;
    }

    // Calculate unit price (price per single unit)
    const unitPriceInCents = Math.round(variantPrice / quantity);
    const formattedUnitPrice = this.formatMoney(unitPriceInCents, moneyFormat);

    // Build final string: "5lb – $135.00 ($27.00/lb)"
    return `${quantityUnitPart} – ${formattedVariantPrice} (${formattedUnitPrice}/${unit})`;
  }

  /**
   * Format money using Shopify's money format
   * @param {number} cents - Price in cents
   * @param {string} format - Shopify money format (e.g., "${{amount}}")
   * @returns {string} - Formatted price string
   */
  static formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = UnitPricing;
}

// Make available globally
window.UnitPricing = UnitPricing;
